import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import req from 'utils/req';
import { z } from 'zod';
import { useI18n } from '~/stores/i18n';

const ExperienceReviewSchema = z
  .object({
    id: z.string().uuid(),
    experience_id: z.string().uuid().nullable(),
    propositionId: z.string().uuid().nullable(),
    title: z.string(),
    title_translated: z.string().nullable(),
    experience_title: z.string(),
    experience_title_translated: z.string().nullable(),
    host_id: z.string().uuid(),
    host_pretty_url: z.string(),
    host_name: z.string(),
    host_picture: z.string().url(),
    host_title: z.string(),
    guest_id: z.string().uuid(),
    guest_pretty_url: z.string(),
    guest_name: z.string(),
    guest_picture: z.string().url(),
    guest_country_iso: z.string().nullable(),
    guest_country_name: z.string().nullable(),
    rating: z.number().int().min(1).max(5),
    scale: z.number().int().min(1).max(5),
    comment: z.string().nullable(),
    comment_translated: z.string().nullable(),
    detected_language: z.string().nullable(),
    votesUp: z.number().int().nonnegative(),
    votedByCurrentUser: z.boolean(),
    created: z.string(),
  })
  .partial();

export type ExperienceReview = z.infer<typeof ExperienceReviewSchema>;

async function getExperienceReviews({
  experienceId,
  pageParam,
  limit,
  totalReviews,
  lang,
}) {
  const nextLimit = pageParam + 1 * limit;
  const nextCursor = nextLimit >= totalReviews ? undefined : nextLimit;
  const response = await req(
    `${URL_API_HOST}/api/v1/review/experience/${experienceId}?lang=${lang}&limit=${limit}&from=${pageParam}`,
  ).get();

  const result = ExperienceReviewSchema.array().safeParse(response);
  if (!result.success) {
    console.error(
      'Error fetching experience reviews, but still returning data',
      result.error,
    );
    return {
      nextCursor,
      data: response,
    };
  }

  return {
    nextCursor,
    data: result.data,
  };
}

function useExperienceReviews({ experienceId, limit = 3, totalReviews }) {
  const { lang } = useI18n();
  const queryClient = useQueryClient();
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isLoading } =
    useInfiniteQuery(
      ['experience-reviews', experienceId, limit, lang],
      ({ pageParam = 0 }) =>
        getExperienceReviews({
          experienceId,
          pageParam,
          limit,
          totalReviews,
          lang,
        }),
      {
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        onSuccess: (data) => {
          data.pages.forEach((page) => {
            page.data.forEach((review) => {
              queryClient.setQueryData(
                ['experience-review', review.id],
                review,
              );
            });
          });
        },
      },
    );

  const reviews = useMemo(() => {
    return data?.pages?.flatMap((page) => page.data) || [];
  }, [data]);

  return {
    reviews,
    onLoadMoreReviews: fetchNextPage,
    hasMoreReviews: hasNextPage,
    isLoading: isFetchingNextPage || isLoading,
  };
}

export default useExperienceReviews;
